
import React, { useState, useEffect } from 'react';
import { Image as ImageIcon, AlertCircle, HelpCircle } from 'lucide-react';

interface MediaRendererProps {
  src?: string;
  className?: string;
  alt?: string;
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent) => void;
}

/**
 * Intelligent component that detects media type and renders either an img or video tag.
 * Handles empty, loading, and error states gracefully with helpful feedback.
 */
export const MediaRenderer: React.FC<MediaRendererProps> = ({ src, className, alt, style, onClick }) => {
  const [error, setError] = useState(false);

  // Reset error state if src changes
  useEffect(() => {
    setError(false);
  }, [src]);

  if (!src || src.trim() === '') {
    return (
      <div 
        className={`${className} flex flex-col items-center justify-center bg-slate-900/60 backdrop-blur-md border border-white/5`}
        style={style}
        onClick={onClick}
      >
        <ImageIcon className="w-8 h-8 text-white/10 animate-pulse" />
        <span className="text-[8px] font-bold text-white/20 uppercase tracking-luxury mt-4">Awaiting Media</span>
      </div>
    );
  }

  if (error) {
    return (
      <div 
        className={`${className} flex flex-col items-center justify-center bg-slate-950 border border-red-500/20 px-8 text-center`}
        style={style}
        onClick={onClick}
      >
        <AlertCircle className="w-8 h-8 text-red-500/40 mb-4" />
        <h5 className="text-[10px] font-bold text-red-500/60 uppercase tracking-luxury mb-2">Unsupported Format</h5>
        <p className="text-[9px] text-slate-500 leading-relaxed max-w-[200px]">
          Browsers cannot play HEIC or high-bitrate MOV files. Please use <span className="text-sky-400">JPG, PNG, or MP4</span>.
        </p>
      </div>
    );
  }

  const isVideo = (url: string) => {
    if (url.startsWith('data:video/')) return true;
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov'];
    return videoExtensions.some(ext => url.toLowerCase().includes(ext));
  };

  if (isVideo(src)) {
    return (
      <video
        key={src}
        src={src}
        className={className}
        style={style}
        onClick={onClick}
        autoPlay
        muted
        loop
        playsInline
        aria-label={alt}
        onError={() => setError(true)}
      />
    );
  }

  return (
    <img
      key={src}
      src={src}
      className={className}
      alt={alt}
      style={style}
      onClick={onClick}
      loading="lazy"
      onError={() => setError(true)}
    />
  );
};
